import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "dermatology-web-design-colorado",
  metaTitle: "Dermatology Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom dermatology web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Dermatology Web Design Colorado",
  eyebrow: "Dermatology Web Design — Colorado",
  h1: (
    <>
      Dermatology <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded dermatology websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Dermatology websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first dermatology sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Dermatology web design in Colorado",
    body: "Dermatology web design is the practice of building conversion-focused, mobile-first websites for dermatology businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with online booking above the fold." },
    { title: "Medical + cosmetic service pages", body: "Acne, eczema, skin cancer, Mohs, Botox, fillers, laser — each ranks independently." },
    { title: "Online booking integration", body: "Real-time booking integration with Modernizing Medicine, Nextech, or your EMR." },
    { title: "Provider bio pages", body: "Each dermatologist and PA with credentials, fellowships, and direct booking CTA." },
    { title: "Insurance & self-pay clarity", body: "Accepted plans for medical visits and self-pay pricing for cosmetic surfaced clearly." },
    { title: "Schema and SEO at launch", body: "Physician, MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Patients search by condition or treatment", body: "We build pages for the actual queries — \"acne treatment\", \"skin cancer screening\", \"Mohs surgery\"." },
    { heading: "Medical and cosmetic need separate flows", body: "Insurance-based medical and cash-pay cosmetic are different buyers and different conversion paths." },
    { heading: "Reviews and credentials close the booking", body: "Above-the-fold reviews + board certification drive new-patient conversion." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a dermatology website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a dermatology website?", a: "Typical timeline is 4–6 weeks for a 12–20 page dermatology site with full service and service-area pages. Larger multi-location dermatology businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs dermatology businesses?", a: "No — we build dermatology sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Dermatology Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for dermatology businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/dermatology-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
